import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { C } from "../reporting/reportingStyles";
import { RCard, Badge } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { coordinateStatus, countMissingPins } from "../../utils/coordinateStatus";

/**
 * "Near a branch" lock-screen suggestion for the Apple Wallet card.
 *
 * What the admin controls here, and what they do not:
 *   - Coordinates always come from the branch pins maintained under Branches. Nothing is
 *     typed here; a branch without a usable pin can never trigger and is shown greyed.
 *   - Which branches go on a customer's card: Automatic (default) puts every pinned branch
 *     on the card when there are ten or fewer; with more, each customer gets the ten they
 *     order from most, then flagship stores. Manual pins one fixed list of up to ten for
 *     everyone. Apple's limit is ten per card, full stop.
 *   - The text: one merchant-wide message (EN + AR) with an optional {branch} placeholder,
 *     plus per-branch overrides. Empty everywhere → falls back to the offer text.
 *
 * It is a silent suggestion, not a notification: iOS shows the card on the lock screen with
 * this text when the phone is near; Apple decides when; the customer can turn it off.
 */

export const MAX_LOCATIONS = 10;
export const LOCATION_TEXT_MAX = 100;
export const LOCATION_TEXT_ADVISED = 60;
export const DISTANCE_MIN_M = 50;
export const DISTANCE_MAX_M = 10000;

export const DEFAULT_LOCATION_ALERTS = {
    enabled: false,
    text: "",
    textAr: "",
    maxDistanceM: null,
    branchMode: "auto",
    branchIds: [],
    branchTexts: {},
};

const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box",
};

const pill = (active) => ({
    padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
    border: `1px solid ${active ? C.coral : C.border}`,
    background: active ? C.coralLight : C.white,
    color: active ? C.coralDark : C.textMed,
});

const Counter = ({ value, max = LOCATION_TEXT_MAX, advised = LOCATION_TEXT_ADVISED }) => {
    const n = (value || "").length;
    const tone = n > max ? C.coral : n > advised ? C.orange : C.textLight;
    return <span style={{ fontSize: 11, color: tone, fontWeight: n > advised ? 700 : 400 }}>{n} / {max}</span>;
};

const substitute = (template, name) => (template || "").replace(/\{branch\}/gi, name || "your branch");

/** iOS lock-screen suggestion mock: pass icon, merchant name, text clamped to two lines. */
const LockScreenMock = ({ merchantName, text, dir }) => (
    <div style={{ background: "rgba(120,120,128,0.16)", borderRadius: 16, padding: "10px 12px", display: "flex", gap: 10, alignItems: "center", maxWidth: 360 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }} dir={dir}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{merchantName || "Your brand"}</div>
            <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {text || <em style={{ color: C.textLight }}>Your near-branch text appears here</em>}
            </div>
        </div>
    </div>
);

const PinBadge = ({ branch }) => {
    const s = coordinateStatus(branch);
    const tone = s.tone === "green" ? [C.green, C.greenLight] : s.tone === "orange" ? [C.orange, C.orangeLight] : [C.coral, C.coralLight];
    return <span title={s.hint}><Badge color={tone[0]} bg={tone[1]}>{s.label}</Badge></span>;
};

/**
 * One branch row: name, pin status, include checkbox (manual mode), optional custom text.
 */
const BranchRow = ({ branch, la, onToggleInclude, onSetText, includeDisabled }) => {
    const usable = coordinateStatus(branch).ok;
    const included = la.branchIds.includes(branch.id);
    const override = la.branchTexts?.[branch.id] || la.branchTexts?.[String(branch.id)] || null;
    const [open, setOpen] = useState(Boolean(override));

    return (
        <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.borderLight}`, opacity: usable ? 1 : 0.55 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {la.branchMode === "manual" && (
                    <input
                        type="checkbox"
                        checked={included}
                        disabled={!usable || (!included && includeDisabled)}
                        onChange={() => onToggleInclude(branch.id)}
                        title={!usable ? "No usable map pin" : !included && includeDisabled ? `Apple allows at most ${MAX_LOCATIONS} locations per card` : ""}
                        style={{ width: 16, height: 16, cursor: usable ? "pointer" : "not-allowed" }}
                    />
                )}
                <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                        {branch.name}
                        {branch.arabicName ? <span dir="rtl" style={{ color: C.textLight, fontWeight: 400 }}> · {branch.arabicName}</span> : null}
                        {branch.flagShipStore ? <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: C.coralDark, background: C.coralLight, padding: "2px 6px", borderRadius: 4 }}>FLAGSHIP</span> : null}
                    </div>
                    {!usable && <div style={{ fontSize: 11, color: C.coral }}>No usable map pin — this branch can never trigger a suggestion.</div>}
                </div>
                <PinBadge branch={branch} />
                {usable && (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        style={{ padding: 0, border: "none", background: "none", color: C.coral, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >
                        {override ? "✎ Custom text" : open ? "Hide custom text" : "Use a different text here"}
                    </button>
                )}
            </div>
            {usable && open && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8, paddingLeft: la.branchMode === "manual" ? 28 : 0 }}>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Text (EN) for this branch</span>
                            <Counter value={override?.text} />
                        </div>
                        <input type="text" maxLength={LOCATION_TEXT_MAX} value={override?.text || ""} onChange={(e) => onSetText(branch.id, "text", e.target.value)} placeholder="Leave empty to use the merchant-wide text" style={inputStyle} />
                    </div>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Text (AR)</span>
                            <Counter value={override?.textAr} />
                        </div>
                        <input type="text" dir="rtl" maxLength={LOCATION_TEXT_MAX} value={override?.textAr || ""} onChange={(e) => onSetText(branch.id, "textAr", e.target.value)} placeholder="اختياري" style={inputStyle} />
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * @param {object} props
 * @param {object} props.value               locationAlerts config (see DEFAULT_LOCATION_ALERTS)
 * @param {(next: object) => void} props.onChange
 * @param {Array} props.branches            the merchant's branches (GET /api/branch/admin-all)
 * @param {boolean} props.branchesLoading
 * @param {string} props.offerText          the merchant's offer text (fallback when this text is empty)
 * @param {string} props.merchantName
 */
const LocationAlertsCard = ({ value, onChange, branches = [], branchesLoading, offerText, merchantName }) => {
    const la = { ...DEFAULT_LOCATION_ALERTS, ...(value || {}), branchIds: value?.branchIds || [], branchTexts: value?.branchTexts || {} };
    const set = (key, v) => onChange({ ...la, [key]: v });

    const usableBranches = useMemo(() => branches.filter((b) => coordinateStatus(b).ok), [branches]);
    const missingPins = useMemo(() => countMissingPins(branches), [branches]);
    const sampleBranch = usableBranches[0];

    const toggleInclude = (id) => {
        const has = la.branchIds.includes(id);
        if (has) return set("branchIds", la.branchIds.filter((x) => x !== id));
        if (la.branchIds.length >= MAX_LOCATIONS) return;
        set("branchIds", [...la.branchIds, id]);
    };

    const setBranchText = (id, key, v) => {
        const next = { ...la.branchTexts };
        const entry = { text: "", textAr: "", ...(next[id] || next[String(id)] || {}), [key]: v };
        delete next[String(id)];
        if (!entry.text && !entry.textAr) delete next[id];
        else next[id] = entry;
        set("branchTexts", next);
    };

    const effectiveText = la.text || offerText || "";
    const effectiveTextAr = la.textAr || la.text || offerText || "";
    const enText = substitute(effectiveText, sampleBranch?.name);
    const arText = substitute(effectiveTextAr, sampleBranch?.arabicName || sampleBranch?.name);

    const autoCaption = usableBranches.length <= MAX_LOCATIONS
        ? `All ${usableBranches.length} branch${usableBranches.length === 1 ? "" : "es"} with a valid pin go on every card.`
        : `${usableBranches.length} branches have a pin — more than Apple's ${MAX_LOCATIONS}. Each customer's card gets the ${MAX_LOCATIONS} they order from most, then flagship stores.`;

    return (
        <RCard title="📍 Lock-screen suggestion near branches">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.55, maxWidth: 560 }}>
                    When a customer with the card is <b>near one of your branches</b>, iOS shows the card on their
                    lock screen with this text. Silent — no sound, no banner. Apple decides when it appears, and
                    customers can turn it off per card. Branch locations come from the pins under <b>Branches</b>.
                </div>
                <ToggleSwitch checked={Boolean(la.enabled)} handleToggle={() => set("enabled", !la.enabled)} />
            </div>

            <div style={{ marginTop: 18, opacity: la.enabled ? 1 : 0.65 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Text (English)</div>
                            <Counter value={la.text} />
                        </div>
                        <input
                            type="text"
                            maxLength={LOCATION_TEXT_MAX}
                            value={la.text || ""}
                            onChange={(e) => set("text", e.target.value)}
                            placeholder={offerText ? `Leave empty to reuse the offer: “${offerText}”` : "e.g. You're near {branch} — show your card for a free espresso"}
                            style={inputStyle}
                        />
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 }}>
                            The lock screen shows roughly the first {LOCATION_TEXT_ADVISED}–80 characters — lead with the offer.
                            {" "}<code>{"{branch}"}</code> inserts the branch name.
                        </div>
                    </div>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Text (Arabic)</div>
                            <Counter value={la.textAr} />
                        </div>
                        <input
                            type="text"
                            dir="rtl"
                            maxLength={LOCATION_TEXT_MAX}
                            value={la.textAr || ""}
                            onChange={(e) => set("textAr", e.target.value)}
                            placeholder="اختياري — أنت قريب من {branch}"
                            style={inputStyle}
                        />
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 5 }}>Shown to Arabic-language customers. Leave empty to reuse the English text.</div>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>On the lock screen</div>
                        <LockScreenMock merchantName={merchantName} text={enText} />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Max distance (metres)</div>
                        <input
                            type="number"
                            min={DISTANCE_MIN_M}
                            max={DISTANCE_MAX_M}
                            step={50}
                            value={la.maxDistanceM ?? ""}
                            onChange={(e) => set("maxDistanceM", e.target.value === "" ? null : Number(e.target.value))}
                            placeholder="Apple default"
                            style={inputStyle}
                        />
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 }}>
                            Optional. Apple compares it with its own radius for store cards and uses the smaller — it can only shrink the trigger area, never enlarge it.
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.borderLight}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Which branches</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => set("branchMode", "auto")} style={pill(la.branchMode !== "manual")}>Automatic</button>
                        <button type="button" onClick={() => set("branchMode", "manual")} style={pill(la.branchMode === "manual")}>Manual — pick up to {MAX_LOCATIONS}</button>
                    </div>
                    <div style={{ fontSize: 11, color: C.textLight, marginTop: 6, lineHeight: 1.4 }}>
                        {la.branchMode === "manual"
                            ? `${la.branchIds.length} of ${MAX_LOCATIONS} selected. The same list goes on every customer's card — for a campaign at specific branches.`
                            : autoCaption}
                    </div>

                    {missingPins > 0 && (
                        <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: C.orangeLight, color: C.orange, fontSize: 12, fontWeight: 600 }}>
                            {missingPins} branch{missingPins === 1 ? " has" : "es have"} no usable map pin and can never trigger a suggestion.{" "}
                            <Link to="/branches/branchesList" style={{ color: C.orange, textDecoration: "underline" }}>Fix in Branches</Link>
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        {branchesLoading ? (
                            <div style={{ fontSize: 12, color: C.textMed, padding: "12px 0" }}>Loading branches…</div>
                        ) : branches.length === 0 ? (
                            <div style={{ fontSize: 12, color: C.textMed, padding: "12px 0" }}>This merchant has no active branches.</div>
                        ) : (
                            branches.map((b) => (
                                <BranchRow
                                    key={b.id}
                                    branch={b}
                                    la={la}
                                    onToggleInclude={toggleInclude}
                                    onSetText={setBranchText}
                                    includeDisabled={la.branchIds.length >= MAX_LOCATIONS}
                                />
                            ))
                        )}
                    </div>
                </div>

                {la.textAr || offerText ? (
                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Arabic lock screen</div>
                        <LockScreenMock merchantName={merchantName} text={arText} dir="rtl" />
                    </div>
                ) : null}
            </div>
        </RCard>
    );
};

export default LocationAlertsCard;
